import { Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import {
  ILoggerNoiseHistogramChartData,
  ILoggerNoiseHistogramChartDataPoint,
} from '@app/api/interfaces/chart-data/logger-noise-histogram';

@Injectable({ providedIn: 'root' })
export class LoggerNoiseHistogramMockService extends MockService {
  public get(
    projectId: string,
    measurementId: string
  ): Observable<IApiResponse<ILoggerNoiseHistogramChartData, object>> {
    const values = this.generate(measurementId, 0.5);
    const spread = this.calcSpread(values);
    return this.mockResponse('LOGGER_NOISE_HISTOGRAM:get', { data: { values, spread }, meta: {} }, arguments).pipe(
      delay(this.requestDelay)
    );
  }

  private calcSpread(values: ILoggerNoiseHistogramChartDataPoint[]): number {
    const nonZero = values.filter(v => v.count > 0);
    if (nonZero.length === 0) {
      return 0;
    }
    const maxCountPoint = nonZero.reduce((acc, v) => (v.count > acc.count ? v : acc));
    const minCountPoint = nonZero.reduce((acc, v) => (v.count < acc.count ? v : acc));
    return Math.abs(maxCountPoint.noiseDecibel - minCountPoint.noiseDecibel);
  }

  private generate(measurementId: string, dbStep: number): ILoggerNoiseHistogramChartDataPoint[] {
    const charSum = convertStringInToSumOfAllCharacters(measurementId);
    const dbMin = 0;
    const dbMax = 65;
    const peakDb = 30 + (charSum % 20);
    const maxCount = 30;

    const histogram: ILoggerNoiseHistogramChartDataPoint[] = [];

    for (let db = dbMin; db <= dbMax; db += dbStep) {
      const distance = Math.abs(db - peakDb);
      const base = Math.round(maxCount * Math.exp(-Math.pow(distance, 2) / 8));
      const isTailOutlier = distance > 5 && (charSum * 31 + db * 17) % 11 < 2;
      const tailValue = isTailOutlier ? ((charSum + db) % 3) + 1 : 0;
      const count = base > 0 ? base : tailValue;
      histogram.push({ noiseDecibel: db, count });
    }

    return histogram;
  }
}
